/**
 * Form helpers for create/edit specialty views.
 */
import type { CreateSpecialtyDto, Specialty, UpdateSpecialtyDto } from '@/types/specialty.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Drag-list row; placeholder is the unsaved create item. */
export interface SpecialtySortableItem {
  id: string;
  code: string;
  label: string;
  isPlaceholder?: boolean;
}

/** Empty create form; starts active with no linked levels. */
export function emptyCreateSpecialtyForm(): CreateSpecialtyDto {
  return {
    code: '',
    label: '',
    isActive: true,
    levelIds: [],
  };
}

/** Slugify a label into a code (`é` → `e`, spaces → `_`). */
export function toSpecialtySlug(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Changing the label auto-fills the code unless the user edited it manually. */
export function applySpecialtyLabelChange(
  form: CreateSpecialtyDto,
  label: string,
  codeManuallyEdited: boolean,
): CreateSpecialtyDto {
  return {
    ...form,
    label,
    code: codeManuallyEdited ? form.code : toSpecialtySlug(label),
  };
}

/** Append a level id if it is not already linked. */
export function addSpecialtyLevelId(current: string[], id: string): string[] {
  return current.includes(id) ? current : [...current, id];
}

/** Drop a level id from the linked list. */
export function removeSpecialtyLevelId(current: string[], id: string): string[] {
  return current.filter((item) => item !== id);
}

/** Field errors for label / code (both required). */
export function validateSpecialtyForm(data: {
  label?: string;
  code?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.label?.trim()) errors.label = getErrorMessage('requiredLabel');
  if (!data.code?.trim()) errors.code = getErrorMessage('requiredCode');
  return errors;
}

/** Label and code are both required. */
export function isSpecialtyFormValid(data: { label?: string; code?: string }): boolean {
  return !!data.label?.trim() && !!data.code?.trim();
}

/** Hydrate form + linked level ids from GET specialty. */
export function specialtyToFormData(specialty: Specialty): {
  formData: UpdateSpecialtyDto;
  levelIds: string[];
} {
  return {
    formData: {
      code: specialty.code,
      label: specialty.label,
      isActive: specialty.isActive,
    },
    levelIds: specialty.levelLinks?.map((link) => link.level.id) ?? [],
  };
}

/** Create DTO with trimmed code / label. */
export function buildCreateSpecialtyDto(form: CreateSpecialtyDto): CreateSpecialtyDto {
  return {
    ...form,
    code: form.code.trim(),
    label: form.label.trim(),
  };
}

/** Update DTO including the current linked level ids. */
export function buildUpdateSpecialtyDto(
  form: UpdateSpecialtyDto,
  levelIds: string[],
): UpdateSpecialtyDto {
  return {
    ...form,
    code: form.code?.trim(),
    label: form.label?.trim(),
    levelIds,
  };
}

/** Swap the create placeholder id for the real id after POST (keeps drag order). */
export function resolveCreatedSpecialtyOrder(
  orderedIds: string[] | null,
  placeholderId: string,
  createdId: string,
): string[] | null {
  if (!orderedIds) return null;
  return orderedIds.map((id) => (id === placeholderId ? createdId : id));
}

/** Level chip label (`code — label`). */
export function formatSpecialtyLevelLabel(level: { code: string; label: string }): string {
  return `${level.code} — ${level.label}`;
}

/** Existing specialties plus a create placeholder when `isCreate`. */
export function buildSpecialtySortableItems(
  specialties: Array<Pick<Specialty, 'id' | 'code' | 'label'>>,
  isCreate: boolean,
  newSpecialtyLabel?: string,
  placeholderId = '__new__',
): SpecialtySortableItem[] {
  const existing: SpecialtySortableItem[] = specialties.map((s) => ({
    id: s.id,
    code: s.code,
    label: s.label,
  }));
  if (isCreate) {
    return [
      ...existing,
      {
        id: placeholderId,
        code: '',
        label: newSpecialtyLabel?.trim() || 'Nouvelle spécialité',
        isPlaceholder: true,
      },
    ];
  }
  return existing;
}

/** Central error copy from {@link getErrorMessage}. */
export function getSpecialtyApiError(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}
